import React, { useState } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MessageCircleQuestion } from 'lucide-react';

interface CancelSurveyModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string, feedback?: string) => void;
}

const REASON_KEYS = [
  'too_expensive',
  'not_using',
  'missing_features',
  'found_alternative',
  'temporary',
  'difficult_to_use',
  'other',
] as const;

export default function CancelSurveyModal({ open, onClose, onSubmit }: CancelSurveyModalProps) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');

  if (!open) return null;

  const isOther = selectedReason === 'other';
  const canSubmit = selectedReason && (!isOther || feedback.trim().length > 0);

  const handleSubmit = () => {
    if (!canSubmit || !selectedReason) return;
    onSubmit(selectedReason, feedback.trim() || undefined);
    setSelectedReason(null);
    setFeedback('');
  };

  const handleClose = () => {
    setSelectedReason(null);
    setFeedback('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleClose}
    >
      <div
        className="bg-background rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-full bg-primary/10">
            <MessageCircleQuestion className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">
            {t('plans.cancel.survey.title')}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {t('plans.cancel.survey.description')}
        </p>

        {/* Reason select */}
        <Select
          value={selectedReason ?? undefined}
          onValueChange={(value) => setSelectedReason(value)}
        >
          <SelectTrigger className="w-full mb-4">
            <SelectValue placeholder={t('plans.cancel.survey.selectPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {REASON_KEYS.map((key) => (
              <SelectItem key={key} value={key}>
                {t(`plans.cancel.survey.reasons.${key}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Feedback textarea — visible only after selecting a reason */}
        {selectedReason && (
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={
              isOther
                ? t('plans.cancel.survey.feedbackPlaceholderRequired')
                : t('plans.cancel.survey.feedbackPlaceholder')
            }
            rows={3}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none mb-5"
          />
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={handleClose}>
            {t('plans.cancel.survey.backButton')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {t('plans.cancel.survey.continueButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}
